import React, { useMemo, useState, useCallback, useEffect } from 'react'

// components
import '@aytm/app/swidget/base/questions/Ecommerce/Ecommerce/Store/ModelViewer.ts'
import Carousel from '@aytm/app/swidget/base/questions/Ecommerce/components/Carousel.tsx'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink.tsx'
import Rating from './Rating.tsx'
import AddToCartButton from './AddToCartButton.tsx'
import AssetsModal from './AssetsModal.tsx'

// types
import { IImageAsset } from '@aytm/app/swidget/base/modules/images/types'
import {
  ASSETS_TYPES,
  IEcommerceAnswerSchema,
  IEcommerceQuestionSchema,
  IEcommerceVideoAsset,
  IFileAsset,
  assetsCollectionElement
} from '@aytm/app/swidget/base/questions/Ecommerce/types'

// functions
import { getBigAssetPath, getSmallAssetPath } from '@aytm/lib/common/assets'
import {
  formatPriceWithLocale,
  getAssetType,
  replaceTags
} from '@aytm/app/swidget/base/questions/Ecommerce/functions.ts'
import { unescapeTags } from '@aytm/lib/common/func.js'
import { stripHighlightTags } from '../../utils/stripHighlightTags'

// hooks
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale.ts'
import useTimeline from '@aytm/app/swidget/base/modules/timeline/useTimeline.ts'

// styles
import commonStyle from '../Common.module.scss'
import style from './Product.module.scss'

import Subproducts from './Subproducts.tsx'

interface IProductProps {
  answer: IEcommerceAnswerSchema
  schema: IEcommerceQuestionSchema
  quantity: number
  index: number
  deliveryDate: number
  findability: boolean
  onAddToCart: (qnt: -1 | 1, index: number) => void
  onBuyNow: (index: number) => void
  onItemSelect: (index: number) => void
  onItemClose: () => void
  scrollTop: () => void
}

const Product = ({
  answer,
  schema,
  quantity,
  index,
  deliveryDate,
  findability,
  onAddToCart,
  onBuyNow,
  onItemSelect,
  onItemClose,
  scrollTop
}: IProductProps) => {
  // custom hooks calls
  const timeline = useTimeline()
  const { t, languageTag } = useSurveyLocale()
  const viewport = useViewport()

  // variables
  const isDiscounted = answer.discounted_price > 0 && answer.price > answer.discounted_price

  // memoized
  const hasVideos = useMemo(() => answer.gallery?.some(asset => 'video' in asset), [answer])
  const localDate = useMemo(
    () =>
      new Date(deliveryDate).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      }),
    [deliveryDate]
  )
  const assetsCollection: assetsCollectionElement[] = useMemo(
    () => [
      ...(answer.image ? [answer.image] : []),
      ...(answer.gallery || []),
      ...(answer.view3d ? [answer.view3d] : [])
    ],
    [answer]
  )
  const lastImageAsset = useMemo(
    () =>
      assetsCollection
        ?.slice()
        .reverse()
        .find((asset): asset is IImageAsset => 'original_url' in asset),
    [assetsCollection]
  )

  // state
  const [position, setPosition] = useState<null | { x: number; y: number }>(null)
  const [openAssetsModal, setOpenAssetsModal] = useState<boolean>(false)
  const [onImage, setOnImage] = useState<boolean>(false)
  const [showDescription, setShowDescription] = useState<boolean>(false)
  const [selectedAssetIndex, setSelectedAssetIndex] = useState<number>(() => {
    const defaultGalleryIndex = answer.gallery?.findIndex(img => img.product?.default)
    if (defaultGalleryIndex !== undefined && defaultGalleryIndex !== -1) {
      return defaultGalleryIndex + 1
    }
    if (answer.view3d?.default) {
      return assetsCollection.length - 1
    }
    return 0
  })

  // functions
  const onAssetSelect = useCallback((index: number) => {
    setSelectedAssetIndex(index)
  }, [])
  const onMoveOnImage = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!onImage) {
        timeline.log({ ref: answer.id, target: selectedAssetIndex.toString(), type: 'zoom_image_in' })
        setOnImage(true)
      }
      const element = event.currentTarget
      requestAnimationFrame(() => {
        const { left, top, width, height } = element.getBoundingClientRect()
        const centerX = width / 2
        const centerY = height / 2
        const mouseX = event.clientX - left
        const mouseY = event.clientY - top

        setPosition({
          x: -(mouseX - centerX) * 0.75,
          y: -(mouseY - centerY) * 0.75
        })
      })
    },
    [onImage, timeline, selectedAssetIndex, answer.id]
  )
  const slide = (qnt: -1 | 1) => {
    const nextIndex = selectedAssetIndex + qnt
    if (nextIndex >= 0 && nextIndex < assetsCollection.length) {
      setSelectedAssetIndex(nextIndex)
    }
  }
  const resetImageState = () => {
    requestAnimationFrame(() => {
      if (ASSETS_TYPES.IMAGE === getAssetType(assetsCollection[selectedAssetIndex]) && onImage) {
        timeline.log({ ref: answer.id, target: selectedAssetIndex.toString(), type: 'zoom_image_out' })
      }
      setOnImage(false)
      setPosition({ x: 0, y: 0 })
    })
  }
  const onAssetsModalOpen = (assetIndex: number) => {
    timeline.log({
      ref: answer.id,
      type: `open_${getAssetType(assetsCollection[assetIndex]).toLowerCase()}_dialog`
    })
    setSelectedAssetIndex(assetIndex)
    setOpenAssetsModal(true)
  }
  const onImageEnter = () => {
    if (!onImage) {
      timeline.log({ ref: answer.id, target: selectedAssetIndex.toString(), type: 'zoom_image_in' })
    }
    setOnImage(true)
  }
  const onModalClose = (assetType: ASSETS_TYPES) => {
    timeline.log({
      ref: answer.id,
      type: `close_${assetType.toLowerCase()}_dialog`
    })
    setOpenAssetsModal(false)
  }

  // effects
  useEffect(() => {
    scrollTop()
  }, [scrollTop])
  useEffect(() => {
    if (viewport.mobile) return
    timeline.log({
      ref: answer.id,
      target: selectedAssetIndex.toString(),
      type: 'show_asset',
      context:
        ASSETS_TYPES.VIDEO === getAssetType(assetsCollection[selectedAssetIndex])
          ? 'video'
          : ASSETS_TYPES.IMAGE === getAssetType(assetsCollection[selectedAssetIndex])
            ? 'image'
            : 'model'
    })
  }, [selectedAssetIndex, assetsCollection, timeline, answer.id, viewport.mobile])

  // render functions
  const onSubitemSelect = (index: number) => {
    timeline.log({
      type: 'close_popup',
      ref: schema.id
    })
    // AYTM-12710 OQ-PF-32: the gallery reset is owned by the keyed remount in WalmartView — the detail
    // `<Product>` is `key`ed by the selected id, so switching variants remounts it and the
    // `selectedAssetIndex` initializer recomputes the TARGET variant's default. No manual reset here
    // (the old one read the OUTGOING `answer`'s default and applied it to the new gallery → wrong image).
    onItemSelect(index)
  }

  const renderAsset = (asset: assetsCollectionElement, index: number) => (
    <>
      {ASSETS_TYPES.IMAGE === getAssetType(asset) && (
        <div
          onMouseEnter={() => onAssetSelect(index)}
          className={style.imagePreview}
          role="img"
          aria-label={(asset as IImageAsset).alt_text || t('questions', 'img', 'noAlt')}
          style={{ backgroundImage: `url(${getSmallAssetPath(asset as IImageAsset)})` }}
        />
      )}
      {ASSETS_TYPES.MODEL === getAssetType(asset) && (
        <div className={style.modelPreviewWrapper}>
          <model-viewer
            src={(asset as IFileAsset).file_url}
            camera-controls={false}
            interaction-prompt="none"
            style={{ width: '86px', height: '86px' }}
          />
          <div onMouseEnter={() => onAssetSelect(index)} className={style.modelPreview} />
        </div>
      )}
      {ASSETS_TYPES.VIDEO == getAssetType(asset) && (
        <div
          className={style.videoPreview}
          onMouseEnter={() => onAssetSelect(index)}
          role="presentation"
          style={{ backgroundImage: `url(${(asset as IEcommerceVideoAsset).video?.thumbnail})` }}
        >
          <i className="wm-Play" />
        </div>
      )}
    </>
  )

  return (
    <section className={`${style.wrapper} ${viewport.mobile ? style.wrapperMobile : ''}`} data-testid="SW-EC-PDP">
      {viewport.mobile ? (
        <div className={style.productHead}>
          <div className={style.top}>
            <div className={style.caption}>
              {answer.sub_title && <EmptyLink color="#0053e2">{replaceTags(answer.sub_title)}</EmptyLink>}
            </div>
            <div className={style.ratingWrapperMobile}>
              {answer.rating ? (
                <>
                  <EmptyLink color="#0053e2">
                    <Rating rating={answer.rating} color="#ffc220" />
                  </EmptyLink>
                  <span className={style.ratingNumeric}>({answer.rating})</span>
                  <span className={style.reviews}>
                    <EmptyLink color="#0053e2">{answer.reviews}</EmptyLink>
                  </span>
                </>
              ) : (
                <div className={style.noRating}>
                  <i className="wm-Star" />
                  <span>({t('questions', 'ec', 'walmart', 'noRating')})</span>
                </div>
              )}
            </div>
          </div>
          <div className={style.description}>
            <div
              className={showDescription ? style.expanded : style.collapsed}
              dangerouslySetInnerHTML={{ __html: answer.text }}
            />
            {answer.text.length > 92 && (
              <button className={style.show} onClick={() => setShowDescription(!showDescription)}>
                <i className={showDescription ? 'wm-ChevronUp' : 'wm-ChevronDown'} />
              </button>
            )}
          </div>
          <div className={style.options}>
            <div className={style.returns}>
              <i className="wm-Truck" />
              <span className={style.returnText}>{t('questions', 'ec', 'walmart', 'freeShipping')}</span>
            </div>
            <div className={style.returns}>
              <div className={style.returnImage}></div>
              <span className={style.returnText}>{t('questions', 'ec', 'walmart', 'free90days')}</span>
            </div>
          </div>
          <div className={style.labels}>
            {isDiscounted && <div className={commonStyle.rollback}>{t('questions', 'ec', 'walmart', 'rollback')}</div>}
            {answer.overal_pick && (
              <div className={commonStyle.bought}>{t('questions', 'ec', 'walmart', 'popularPick')}</div>
            )}
            {answer.best_seller && (
              <div className={commonStyle.best}>{t('questions', 'ec', 'walmart', 'bestSeller')}</div>
            )}
            {answer.red_label && <div className={commonStyle.redLabel}>{unescapeTags(answer.red_label)}</div>}
            {answer.green_label && (
              <div className={commonStyle.greenLabel}>{stripHighlightTags(unescapeTags(answer.green_label))}</div>
            )}
          </div>
        </div>
      ) : (
        <button className={commonStyle.back} onClick={onItemClose}>
          <i className="wm-ChevronLeft" />
          {t('questions', 'ec', 'walmart', 'back')}
        </button>
      )}

      <div className={style.product}>
        {openAssetsModal && (
          <AssetsModal
            answer={answer}
            assetsCollection={assetsCollection}
            currentIndex={selectedAssetIndex}
            onModalClose={(assetType: ASSETS_TYPES) => onModalClose(assetType)}
          />
        )}
        <div className={style.imageWrapper}>
          {viewport.mobile && assetsCollection.length ? (
            <Carousel
              key={schema.id}
              schema={answer}
              store="walmart"
              pagination="walmart"
              hasVideos={hasVideos}
              openDialog={assetIndex => onAssetsModalOpen(assetIndex)}
            />
          ) : (
            <div className={style.images}>
              {assetsCollection.length > 0 && (
                <div className={style.imagesList}>
                  {assetsCollection.map((asset, index) =>
                    assetsCollection.length <= 4 ? (
                      <div className={style.imagesListItem} key={index}>
                        {renderAsset(asset, index)}
                      </div>
                    ) : (
                      <div className={style.imagesListItem} key={index}>
                        {index < 4 && renderAsset(asset, index)}
                      </div>
                    )
                  )}
                  {assetsCollection.length > 4 && (
                    <div
                      className={style.seeMore}
                      style={{
                        backgroundImage: `url(${
                          ASSETS_TYPES.IMAGE === getAssetType(assetsCollection[4])
                            ? getSmallAssetPath(lastImageAsset)
                            : ''
                        })`
                      }}
                      onClick={() => onAssetsModalOpen(0)}
                    >
                      <span className={style.count}>
                        <span className={style.plus}>+</span>
                        <strong>{assetsCollection.length - 4}</strong>
                      </span>
                      <span className={style.text}>{t('questions', 'ec', 'walmart', 'viewAll')}</span>
                    </div>
                  )}
                </div>
              )}
              <div className={style.bigImage} style={{ border: `1px solid ${onImage ? 'rgb(45, 45, 45)' : '#fff'}` }}>
                <div className={style.imageActions}>
                  <EmptyLink color="#0053e2">
                    <div className={style.iconWrapper}>
                      <i className="wm-Heart" />
                    </div>
                  </EmptyLink>
                  <EmptyLink color="#0053e2">
                    <div className={style.iconWrapper}>
                      <i className="wm-Share" />
                    </div>
                  </EmptyLink>
                  {assetsCollection[selectedAssetIndex] ? (
                    <button
                      className={style.iconWrapper}
                      disabled={ASSETS_TYPES.VIDEO === getAssetType(assetsCollection[selectedAssetIndex])}
                      onClick={() => onAssetsModalOpen(selectedAssetIndex)}
                    >
                      <i className="wm-ZoomIn" />
                    </button>
                  ) : null}
                  {hasVideos ? (
                    <button
                      className={style.iconWrapper}
                      disabled={ASSETS_TYPES.VIDEO !== getAssetType(assetsCollection[selectedAssetIndex])}
                      onClick={() => onAssetsModalOpen(selectedAssetIndex)}
                    >
                      <i className="wm-Play" />
                    </button>
                  ) : null}
                </div>
                {selectedAssetIndex < assetsCollection.length - 1 && (
                  <button className={style.next} onClick={() => slide(1)}>
                    <i className="wm-ChevronRight" />
                  </button>
                )}
                {selectedAssetIndex > 0 && (
                  <button className={style.prev} onClick={() => slide(-1)}>
                    <i className="wm-ChevronLeft" />
                  </button>
                )}

                {assetsCollection.length ? (
                  <>
                    {ASSETS_TYPES.IMAGE === getAssetType(assetsCollection[selectedAssetIndex]) && (
                      <div
                        className={style.mainImage}
                        role="img"
                        aria-label={
                          (assetsCollection[selectedAssetIndex] as IImageAsset).alt_text ||
                          t('questions', 'img', 'noAlt')
                        }
                        style={{
                          backgroundImage: `url(${getBigAssetPath(assetsCollection[selectedAssetIndex] as IImageAsset, 'transparent')})`,
                          backgroundPosition: position
                            ? `calc(50% + ${position.x}px) calc(50% + ${position.y}px)`
                            : '50% 50%',
                          transform: `scale(${onImage ? 2 : 1})`
                        }}
                        onMouseMove={onMoveOnImage}
                        onMouseEnter={onImageEnter}
                        onMouseLeave={resetImageState}
                      />
                    )}
                    {ASSETS_TYPES.MODEL === getAssetType(assetsCollection[selectedAssetIndex]) && (
                      <div className={style.viewerWrapper} onClick={() => onAssetsModalOpen(selectedAssetIndex)}>
                        <model-viewer
                          style={{ height: '100%' }}
                          src={(assetsCollection[selectedAssetIndex] as IFileAsset).file_url}
                          ar
                          camera-controls
                          touch-action="pan-y"
                        />
                      </div>
                    )}
                    {ASSETS_TYPES.VIDEO === getAssetType(assetsCollection[selectedAssetIndex]) && (
                      <div
                        className={style.mainVideo}
                        onClick={() => onAssetsModalOpen(selectedAssetIndex)}
                        role="presentation"
                        style={{
                          backgroundImage: `url(${
                            (assetsCollection[selectedAssetIndex] as IEcommerceVideoAsset).video?.thumbnail
                          })`
                        }}
                      >
                        <div className={style.playWrapper}>
                          <i className="wm-Play" />
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className={style.noImage} />
                )}
              </div>
            </div>
          )}
        </div>
        <div className={style.content}>
          {!viewport.mobile && (
            <>
              <div className={style.labels}>
                {/* {answer.bought && <div className={commonStyle.bought}>{unescapeTags(answer.bought)}</div>} */}
                {isDiscounted && (
                  <div className={commonStyle.rollback}>{t('questions', 'ec', 'walmart', 'rollback')}</div>
                )}
                {answer.overal_pick && (
                  <div className={commonStyle.bought}>{t('questions', 'ec', 'walmart', 'popularPick')}</div>
                )}
                {answer.best_seller && (
                  <div className={commonStyle.best}>{t('questions', 'ec', 'walmart', 'bestSeller')}</div>
                )}
                {answer.red_label && <div className={commonStyle.redLabel}>{unescapeTags(answer.red_label)}</div>}
                {answer.green_label && (
                  <div className={commonStyle.greenLabel}>{stripHighlightTags(unescapeTags(answer.green_label))}</div>
                )}
              </div>
              {answer.sub_title && (
                <EmptyLink color="#0053e2">
                  <p className={style.visitStore}>{replaceTags(answer.sub_title)}</p>
                </EmptyLink>
              )}
              <h6 dangerouslySetInnerHTML={{ __html: answer.text }} />
              <div className={style.ratingWrapper}>
                {answer.rating ? (
                  <>
                    <Rating rating={answer.rating} color="#ffc220" />
                    <span className={style.ratingNumeric}>({answer.rating})</span>
                    <span className={style.line}>|</span>
                    <span className={style.reviews}>
                      <EmptyLink color="#0053e2">
                        {answer.reviews}&nbsp;{1 === answer.reviews ? 'rating' : 'ratings'}
                      </EmptyLink>
                    </span>
                  </>
                ) : (
                  <div className={style.noRating}>
                    <i className="wm-Star" />
                    <span>({t('questions', 'ec', 'walmart', 'noRating')})</span>
                  </div>
                )}
              </div>
            </>
          )}
          <Subproducts answer={answer} schema={schema} onSubitemSelect={onSubitemSelect} />
          {(answer.description || answer.feature_bullets) && (
            <div className={`${style.about}`}>
              <div className={style.aboutTitle}>
                {t('questions', 'ec', 'walmart', 'aboutItem')}
                <EmptyLink color="#0053e2">
                  <i className="pi pi-chevron-up" />
                </EmptyLink>
              </div>
              <div
                className={style.description}
                dangerouslySetInnerHTML={{
                  __html: `${replaceTags(answer.feature_bullets || '')} ${replaceTags(answer.description || '')}`
                }}
              />
              <EmptyLink color="#0053e2">
                <div className={style.viewMore}>
                  <span>{t('questions', 'ec', 'walmart', 'viewMore')}</span>
                  <i className={'wm-ChevronDown'} />
                </div>
              </EmptyLink>
            </div>
          )}
          <div className={style.about}>
            <div className={style.aboutTitle}>
              {t('questions', 'ec', 'walmart', 'specs')}
              <EmptyLink color="#0053e2">
                <i className="pi pi-chevron-down" />
              </EmptyLink>
            </div>
          </div>
          <div className={style.about}>
            <div className={style.aboutTitle}>
              {t('questions', 'ec', 'walmart', 'reviewsSummary')}
              <EmptyLink color="#0053e2">
                <i className="pi pi-chevron-down" />
              </EmptyLink>
            </div>
          </div>
        </div>
        <div className={style.buy} data-testid="SW-EC-PDP-AddToCartBlock">
          <div className={style.price}>
            {schema.answers[index].discounted_price &&
            schema.answers[index].price > schema.answers[index].discounted_price ? (
              <>
                <span
                  className={style.greenPrice}
                  dangerouslySetInnerHTML={{
                    __html: `${t('questions', 'ec', 'walmart', 'now')} ${formatPriceWithLocale(
                      schema.answers[index].discounted_price,
                      schema.price_mask,
                      languageTag,
                      true,
                      false
                    )}`
                  }}
                />
                <span
                  className={style.lastPrice}
                  dangerouslySetInnerHTML={{
                    __html: formatPriceWithLocale(
                      schema.answers[index].price,
                      schema.price_mask,
                      languageTag,
                      true,
                      false
                    )
                  }}
                ></span>
              </>
            ) : (
              <span
                className={style.regularPrice}
                dangerouslySetInnerHTML={{
                  __html: formatPriceWithLocale(
                    schema.answers[index].price,
                    schema.price_mask,
                    languageTag,
                    true,
                    false
                  )
                }}
              ></span>
            )}
            {answer.per_count_price && (
              <div className={style.perCount}>
                <EmptyLink color="#0053e2">
                  <i className="wm-InfoCircle" />
                </EmptyLink>
                <span>{unescapeTags(answer.per_count_price)}</span>
              </div>
            )}
          </div>
          {schema.answers[index].discounted_price &&
          schema.answers[index].price > schema.answers[index].discounted_price ? (
            <div className={style.youSave}>
              <div className={style.saveTitle}>{t('questions', 'ec', 'walmart', 'youSave')}</div>
              <div
                className={style.savePrice}
                dangerouslySetInnerHTML={{
                  __html: formatPriceWithLocale(
                    schema.answers[index].price - schema.answers[index].discounted_price,
                    schema.price_mask,
                    languageTag,
                    true,
                    false
                  )
                }}
              />
            </div>
          ) : null}

          <span className={style.note}>
            <span className={style.text}>{t('questions', 'ec', 'walmart', 'purchasedOnline')}</span>
            <EmptyLink color="#0053e2">
              <i className="wm-InfoCircle" />
            </EmptyLink>
          </span>
          <div className={style.options}>
            <div className={style.returns}>
              <i className="wm-Truck" />
              <span className={style.returnText}>{t('questions', 'ec', 'walmart', 'freeShipping')}</span>
            </div>
            <div className={style.returns}>
              <div className={style.returnImage}></div>
              <span className={style.returnText}>{t('questions', 'ec', 'walmart', 'free90days')}</span>
            </div>
          </div>
          <button
            disabled={
              (1 === quantity && findability) || (schema.max_qty_per_product && quantity >= schema.max_qty_per_product)
            }
            className={`${style.buyNow} ${
              (1 === quantity && findability) || (schema.max_qty_per_product && quantity >= schema.max_qty_per_product)
                ? style.disabled
                : ''
            }`}
            onClick={() => {
              onBuyNow(index)
            }}
          >
            {t('questions', 'ec', 'walmart', 'buyNow')}
          </button>
          <div className={style.addToCart}>
            <AddToCartButton
              index={index}
              onAddToCart={onAddToCart}
              amount={quantity}
              defaultLabel={t('questions', 'ec', 'walmart', 'addToCart')}
              findability={findability}
              showIcon={false}
              limit={schema.max_qty_per_product}
              collapsible={false}
              width="100%"
              skin="transparent"
            />
          </div>
          <div className={style.delivery}>
            <p dangerouslySetInnerHTML={{ __html: t('questions', 'ec', 'walmart', 'howGet') }} />
            <EmptyLink color="#0053e2">
              <div className={style.deliveryOptions}>
                <div className={`${style.option} ${style.optionActive}`}>
                  <div className={`${style.image} ${style.image1}`}></div>
                  <div className={style.title}>{t('questions', 'ec', 'walmart', 'shipping')}</div>
                  <div className={style.date}>
                    {t('questions', 'ec', 'walmart', 'arrives')} <strong>{localDate}</strong>
                  </div>
                </div>
                <div className={style.option}>
                  <div className={`${style.image} ${style.image2}`}></div>
                  <div className={`${style.title} ${style.titleDisabled}`}>
                    {t('questions', 'ec', 'walmart', 'pickup')}
                  </div>
                  <div className={`${style.date} ${style.dateDisabled}`}>
                    {t('questions', 'ec', 'walmart', 'notAvailable')}
                  </div>
                </div>
                <div className={style.option}>
                  <div className={`${style.image} ${style.image3}`}></div>
                  <div className={`${style.title} ${style.titleDisabled}`}>
                    {t('questions', 'ec', 'walmart', 'delivery')}
                  </div>
                  <div className={`${style.date} ${style.dateDisabled}`}>
                    {t('questions', 'ec', 'walmart', 'notAvailable')}
                  </div>
                </div>
              </div>
            </EmptyLink>
            <div className={style.deliveryTo}>
              <strong>{t('questions', 'ec', 'walmart', 'myAddress')}</strong>
              <span className={style.change}>
                <EmptyLink color="#0053e2">{t('questions', 'ec', 'walmart', 'change')}</EmptyLink>
              </span>
            </div>
            <div className={style.arrives}>
              <span dangerouslySetInnerHTML={{ __html: t('questions', 'ec', 'walmart', 'arrivesBy') }}></span>
              <strong>{localDate}</strong>
            </div>
            <div className={style.sold}>
              <div className={style.returns}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16px" height="16px" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="m8.225 13.132l-4.776 2.217a1.22 1.22 0 0 0-.134 1.656c.384.655 1.056.968 1.5.706L9.134 14.7a.86.86 0 0 0 .031-1.066a.854.854 0 0 0-.94-.502m7.552.001c-.3-.118-.7.1-.942.505a.87.87 0 0 0 .026 1.06l4.315 3.018c.448.256 1.123-.055 1.507-.711s.309-1.4-.135-1.656zM12 15.271a.856.856 0 0 0-.906.556l-.466 5.235c0 .518.606.938 1.372.938s1.371-.42 1.371-.938l-.462-5.235a.86.86 0 0 0-.909-.556m2.835-4.904a.88.88 0 0 0 .942.509l4.776-2.221c.444-.262.51-.995.135-1.654s-1.059-.97-1.507-.714L14.866 9.3a.88.88 0 0 0-.026 1.07ZM4.819 6.289c-.448-.262-1.12.05-1.5.71a1.22 1.22 0 0 0 .134 1.656l4.772 2.215a.854.854 0 0 0 .94-.5a.86.86 0 0 0-.031-1.065ZM12 2c-.766 0-1.372.424-1.372.943l.472 5.234a.856.856 0 0 0 .9.556a.86.86 0 0 0 .909-.556l.462-5.234C13.372 2.424 12.762 2 12 2"
                  ></path>
                </svg>
                <span className={style.returnText}>{t('questions', 'ec', 'walmart', 'soldBy')}</span>
              </div>
              <div className={style.returns}>
                <div className={style.returnImage}></div>
                <span className={`${style.returnText} ${style.returnTextBold}`}>
                  {t('questions', 'ec', 'walmart', 'free90days')}
                </span>
                <EmptyLink color="#0053e2">
                  <div className={style.details}>{t('questions', 'ec', 'walmart', 'details')}</div>
                </EmptyLink>
              </div>
            </div>
            {viewport.mobile && (
              <>
                <div className={style.quickView}>
                  <div className={style.quick}>{t('questions', 'ec', 'walmart', 'quickView')}</div>
                  <EmptyLink color="#0053e2">
                    <div className={style.detail}>{t('questions', 'ec', 'walmart', 'details')}</div>
                  </EmptyLink>
                  <EmptyLink color="#0053e2">
                    <div className={style.detail}>{t('questions', 'ec', 'walmart', 'specifications')}</div>
                  </EmptyLink>
                  <EmptyLink color="#0053e2">
                    <div className={style.detail}>{t('questions', 'ec', 'walmart', 'reviews')}</div>
                  </EmptyLink>
                </div>
                <div className={style.giftEligible}>
                  <i className="wm-Gift" />
                  <div className={style.text}>{t('questions', 'ec', 'walmart', 'itemEligible')}</div>
                  <EmptyLink color="#0053e2">
                    <div className={style.link}>{t('questions', 'ec', 'walmart', 'learnMore')}</div>
                  </EmptyLink>
                </div>
              </>
            )}
          </div>
          <div className={style.last}>
            <EmptyLink color="#0053e2">
              <div className={style.lastItem}>
                <i className="wm-Heart" />
                <span>{t('questions', 'ec', 'walmart', 'addToList')}</span>
              </div>
            </EmptyLink>
            <EmptyLink color="#0053e2">
              <div className={style.lastItem}>
                <i className="wm-Gift" />
                <span>{t('questions', 'ec', 'walmart', 'addToRegistry')}</span>
              </div>
            </EmptyLink>
          </div>
        </div>
      </div>
      {viewport.mobile && (
        <div className={style.buyButttons} data-testid="SW-EC-PDP-AddToCartBlock">
          <button
            disabled={
              (1 === quantity && findability) || (schema.max_qty_per_product && quantity >= schema.max_qty_per_product)
            }
            className={`${style.buyNow} ${
              (1 === quantity && findability) || (schema.max_qty_per_product && quantity >= schema.max_qty_per_product)
                ? style.disabled
                : ''
            }`}
            onClick={() => onBuyNow(index)}
          >
            {t('questions', 'ec', 'walmart', 'buyNow')}
          </button>
          <AddToCartButton
            index={index}
            onAddToCart={onAddToCart}
            amount={quantity}
            defaultLabel={t('questions', 'ec', 'walmart', 'addToCart')}
            findability={findability}
            showIcon={false}
            collapsible={false}
            limit={schema.max_qty_per_product}
            width="150px"
          />
        </div>
      )}
    </section>
  )
}

export default Product
